(function () {
    'use strict';

    /* Restores the items of a past order into the real (cookie-based) cart, replacing Shopify's
       native "Buy again" — that native flow only carries {variant_id, quantity} forward and drops
       line-item properties, which breaks Meterware cuts (length lives in the Länge/_length_cm/_cut
       properties, not in quantity). See reorder-fix-konzept.md at the repo root.

       "Erneut kaufen" replaces the cart rather than adding to it, so the cart is cleared first.

       The order-reorder customer-account extension (naehwelt-core-app) hands the reconstructed
       line items to this page as a base64url-encoded JSON array in the `nwf_reorder` query param:
       [{ v: variantId, q: quantity, p: properties }, ...]. This script decodes it and adds each
       item with its OWN /cart/add.js call, one at a time (not one combined multi-item call):
       Shopify's Ajax Cart API rejects a combined multi-item add ATOMICALLY if even one item is
       unavailable (sold out, etc.) — nothing at all would be added. Adding one at a time lets an
       unavailable item's own request fail without blocking the rest, while every item still gets
       its full properties (including a distinct `_cut`) from the very same request that creates
       its line, so there is no window in which same-variant Meterware cuts could be merged
       before they are told apart. See the security section of reorder-fix-konzept.md for the
       full reasoning, including why this can't be replicated via the same mechanism Shopify's
       own "Buy again" uses (that path only carries properties for the first line of a link). */

    var QUERY_PARAM = 'nwf_reorder';
    var MAX_QUANTITY = 999;

    function themeReady() {
        return !!(window.FoxTheme && FoxTheme.routes && FoxTheme.utils && FoxTheme.pubsub);
    }

    function collectSections() {
        var sections = [];
        document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } }),
        );
        return sections;
    }

    /* Mirrors nwf-free-gift.js's renderCart(): publishing cartUpdate alone does nothing unless
       cart.sections is filled, so the section HTML is fetched separately via the Section
       Rendering API from the current path. A single item's own /cart/add.js response can't be
       reused for this any more now that items are added one at a time (its `sections` would
       only reflect the cart as of THAT one item, not the final result after every item in the
       list has had its turn) — one fresh, authoritative fetch after the whole sequence finishes
       is simpler and always correct, whichever items succeeded or failed. */
    function fetchCartState(ids) {
        return Promise.all([
            fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(','))).then(
                function (response) {
                    return response.json();
                },
            ),
            fetch('/cart.js').then(function (response) {
                return response.json();
            }),
        ]).then(function (results) {
            var cart = results[1];
            cart.sections = results[0];
            return cart;
        });
    }

    function publishCart(cart) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
    }

    /* Resolves to the number of Meterware cuts removed again for lack of stock (NWF-221). */
    function renderCart() {
        var ids = collectSections();
        if (!ids.length) {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            return Promise.resolve(0);
        }
        return fetchCartState(ids)
            .then(function (cart) {
                var unfulfillable = unfulfillableMeterwareLines(cart.sections);
                if (!unfulfillable.length) {
                    publishCart(cart);
                    return 0;
                }
                return removeLines(cart, unfulfillable)
                    .then(function () {
                        return fetchCartState(ids);
                    })
                    .then(function (updated) {
                        publishCart(updated);
                        return unfulfillable.length;
                    });
            })
            .catch(function (error) {
                console.error('[nwf-reorder] re-render failed, falling back to cart:refresh', error);
                document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
                return 0;
            });
    }

    /* NWF-221: Shopify adds a Meterware cut as quantity 1, so its own stock check passes as long
       as one unit is left. A colour with less stock than one minimum cut is sold out (NWF-216),
       so its cuts are removed again. A cut that is merely longer than the stock stays in the cart
       at its ordered length: the cart marks it and locks the checkout until the customer adjusts
       it. The server-rendered cart line (nwf-cart-price) carries the stock in length steps and
       the minimum. Returns the 1-based line numbers to remove. */
    function unfulfillableMeterwareLines(sections) {
        var lines = {};
        Object.keys(sections || {}).forEach(function (id) {
            var html = sections[id];
            if (typeof html !== 'string') {
                return;
            }
            var doc = new DOMParser().parseFromString(html, 'text/html');
            // main-cart renders every line twice (desktop and mobile), the line number dedupes.
            doc.querySelectorAll('.nwf-cart-price[data-nwf-line]').forEach(function (element) {
                lines[element.dataset.nwfLine] = element.dataset;
            });
        });

        return Object.keys(lines)
            .map(Number)
            .sort(function (a, b) {
                return a - b;
            })
            .filter(function (line) {
                var data = lines[line];
                var step = parseInt(data.nwfStep, 10) || 0;
                if (step <= 0 || data.nwfMax === undefined || data.nwfMax === '') {
                    return false;
                }
                var minUnits = Math.max(1, Math.ceil((parseInt(data.nwfMin, 10) || step) / step));
                return (parseInt(data.nwfMax, 10) || 0) < minUnits;
            });
    }

    /* One /cart/update.js call with a quantity per line position, 0 for the removed ones. */
    function removeLines(cart, lineNumbers) {
        var updates = (cart.items || []).map(function (item, index) {
            return lineNumbers.indexOf(index + 1) === -1 ? item.quantity : 0;
        });
        return fetch(
            FoxTheme.routes.cart_update_url || '/cart/update.js',
            Object.assign({}, FoxTheme.utils.fetchConfig(), { body: JSON.stringify({ updates: updates }) }),
        ).then(assertOk);
    }

    /* atob() only decodes to a byte string, so it has to be re-interpreted as UTF-8 for the
       umlaut in "Länge" (and any other non-ASCII property value) to survive the round trip. */
    function base64UrlDecode(value) {
        var padded = value.replace(/-/g, '+').replace(/_/g, '/');
        while (padded.length % 4) {
            padded += '=';
        }
        var bytes = atob(padded);
        var percentEncoded = '';
        for (var i = 0; i < bytes.length; i += 1) {
            percentEncoded += '%' + ('0' + bytes.charCodeAt(i).toString(16)).slice(-2);
        }
        return decodeURIComponent(percentEncoded);
    }

    /* Turns one decoded payload entry into a valid /cart/add.js item, or null if it is malformed
       — a broken entry is dropped rather than aborting the whole reorder. */
    function toCartItem(entry) {
        if (!entry || typeof entry !== 'object') {
            return null;
        }
        var variantId = parseInt(entry.v, 10);
        if (!variantId || variantId <= 0) {
            return null;
        }
        var quantity = parseInt(entry.q, 10);
        if (!quantity || quantity <= 0) {
            quantity = 1;
        }
        quantity = Math.min(quantity, MAX_QUANTITY);

        var item = { id: variantId, quantity: quantity };
        if (entry.p && typeof entry.p === 'object') {
            item.properties = entry.p;
        }
        return item;
    }

    function stripReorderParam() {
        var url = new URL(window.location.href);
        url.searchParams.delete(QUERY_PARAM);
        history.replaceState(history.state, document.title, url.pathname + url.search + url.hash);
    }

    function labelsHolder() {
        return document.querySelector('[data-nwf-reorder-labels]');
    }

    /* The element the overlay and the inline notice are scoped to. Shopify always renders a
       section inside #shopify-section-<id>, but that wrapper spans the full, unconstrained
       width — the cart's actual visible content sits in a narrower descendant (main-cart's own
       `.page-width`, cart-drawer's own `#CartDrawer-<id>`), given explicitly per include via
       `content_selector` (see nwf-reorder.liquid) so the overlay/notice line up with the rest of
       the page instead of spanning edge to edge. Falls back to the section wrapper, and then to
       <body>, if either lookup ever fails — covering more than intended is still better than a
       broken/invisible overlay. */
    function contentRoot() {
        var holder = labelsHolder();
        var sectionId = holder && holder.dataset.sectionId;
        var section = sectionId && document.getElementById('shopify-section-' + sectionId);
        if (!section) {
            return document.body;
        }
        var selector = holder.dataset.contentSelector;
        var content = selector && section.querySelector(selector);
        return content || section;
    }

    /* Renders the message as a persistent notice on the cart page itself instead of an alert()
       popup. Inserted as a direct child of contentRoot() (the same element the loading overlay
       uses) rather than inside the inner #MainCart-<id> / #CartDrawer-<id> container, so a later
       cart re-render — which only replaces THAT inner container's innerHTML (see cart.js) —
       never removes it. */
    function showInlineNotice(message) {
        if (!message) {
            return;
        }
        var holder = labelsHolder();
        var root = contentRoot();

        var notice = document.createElement('div');
        notice.className = 'nwf-reorder-notice';
        notice.setAttribute('role', 'alert');

        var text = document.createElement('p');
        text.className = 'nwf-reorder-notice__text';
        text.textContent = message;
        notice.appendChild(text);

        var close = document.createElement('button');
        close.type = 'button';
        close.className = 'nwf-reorder-notice__close';
        close.setAttribute('aria-label', (holder && holder.dataset.closeLabel) || '×');
        close.textContent = '×';
        close.addEventListener('click', function () {
            notice.remove();
        });
        notice.appendChild(close);

        root.insertBefore(notice, root.firstChild);
    }

    /* Genuinely technical failure — a malformed/undecodable payload — where "please try again"
       is honest wording, since a retry might well fix it (e.g. a one-off client glitch). */
    function showError() {
        showInlineNotice(labelsHolder() && labelsHolder().dataset.error);
    }

    /* Every single item failed to add — by far the most likely cause (see addSingleItem) is
       that none of them are available any more, not a technical fault, so this gets its own,
       honest wording instead of reusing showError()'s "please try again" (retrying changes
       nothing if the items are genuinely gone). */
    function showAllUnavailable() {
        showInlineNotice(labelsHolder() && labelsHolder().dataset.unavailable);
    }

    /* Shown when at least one, but not all, items could not be added again — most commonly
       because an item has since sold out. [count]/[total] are plain bracket placeholders (this
       theme's own convention for JS-side string interpolation, see nwf-color-picker.js's
       reportShortfall/error_short), not Liquid's {{ }} syntax. */
    function showPartialError(count, total) {
        var holder = labelsHolder();
        var template = holder && holder.dataset.partialError;
        if (!template) {
            return;
        }
        showInlineNotice(template.replace('[count]', count).replace('[total]', total));
    }

    function assertOk(response) {
        if (!response.ok) {
            var error = new Error('cart request failed with status ' + response.status);
            error.status = response.status;
            throw error;
        }
        return response;
    }

    /* "Erneut kaufen" replaces the cart, it doesn't add to it — clear() empties existing lines
       before the reordered items are added, using the theme's own clear URL (not hardcoded, so
       it still respects a language-subfolder URL). */
    function clearCart() {
        var holder = labelsHolder();
        var url = (holder && holder.dataset.clearUrl) || '/cart/clear.js';
        return fetch(url, Object.assign({}, FoxTheme.utils.fetchConfig(), { body: JSON.stringify({}) })).then(assertOk);
    }

    /* One item, one request — see the file-level comment for why this isn't a single combined
       call. Resolves to true/false rather than rejecting, so one failure never stops
       addReorderItems() from trying the remaining items. */
    function addSingleItem(item) {
        var body = JSON.stringify({ items: [item] });
        return fetch(FoxTheme.routes.cart_add_url, Object.assign({}, FoxTheme.utils.fetchConfig(), { body: body }))
            .then(assertOk)
            .then(function () {
                return true;
            })
            .catch(function (error) {
                // Far and away the most likely cause is the item having sold out since the
                // original order — logged for diagnosis, but not shown per item; the caller
                // reports one combined count instead.
                console.error('[nwf-reorder] could not add one reordered item back to the cart', error);
                return false;
            });
    }

    function addReorderItems(items) {
        return clearCart().then(function () {
            // Sequential on purpose, not parallel: concurrent writes to the same cart are the
            // kind of race this theme avoids elsewhere (see nwf-free-gift.js's own
            // batched-update comment), and each add already needs to complete before the next
            // one can safely run against the resulting cart state.
            var succeeded = 0;
            var chain = Promise.resolve();
            items.forEach(function (item) {
                chain = chain.then(function () {
                    return addSingleItem(item).then(function (ok) {
                        if (ok) {
                            succeeded += 1;
                        }
                    });
                });
            });

            return chain
                .then(function () {
                    return renderCart();
                })
                .then(function (removed) {
                    succeeded = Math.max(0, succeeded - removed);
                    if (succeeded === 0) {
                        showAllUnavailable();
                    } else if (succeeded < items.length) {
                        showPartialError(items.length - succeeded, items.length);
                    }
                });
        });
    }

    function showLoadingOverlay() {
        var holder = labelsHolder();
        var label = holder && holder.dataset.loading;
        var root = contentRoot();

        var overlay = document.createElement('div');
        overlay.className = 'nwf-reorder-overlay';
        overlay.setAttribute('role', 'status');
        overlay.setAttribute('aria-live', 'polite');

        var spinner = document.createElement('span');
        spinner.className = 'nwf-reorder-overlay__spinner';
        spinner.setAttribute('aria-hidden', 'true');
        overlay.appendChild(spinner);

        if (label) {
            var text = document.createElement('p');
            text.className = 'nwf-reorder-overlay__label';
            text.textContent = label;
            overlay.appendChild(text);
        }

        root.classList.add('nwf-reorder-anchor');
        root.appendChild(overlay);
        return { root: root, overlay: overlay };
    }

    function hideLoadingOverlay(shown) {
        if (!shown) {
            return;
        }
        if (shown.overlay && shown.overlay.parentNode) {
            shown.overlay.parentNode.removeChild(shown.overlay);
        }
        shown.root.classList.remove('nwf-reorder-anchor');
    }

    function init() {
        if (!themeReady()) {
            return;
        }

        var payload = new URLSearchParams(window.location.search).get(QUERY_PARAM);
        if (!payload) {
            return;
        }

        // Stripped immediately, before the add even runs, so a page reload never re-adds the
        // same items twice.
        stripReorderParam();

        var items;
        try {
            items = JSON.parse(base64UrlDecode(payload)).map(toCartItem).filter(Boolean);
        } catch (error) {
            console.error('[nwf-reorder] could not decode the nwf_reorder payload', error);
            showError();
            return;
        }

        if (!items.length) {
            return;
        }

        // Hides the stale (pre-reorder) cart and the briefly-empty cart in between clear and add
        // behind an overlay scoped to the cart section, instead of letting the customer see
        // either flash by.
        var overlay = showLoadingOverlay();
        addReorderItems(items).then(function () {
            hideLoadingOverlay(overlay);
        });
    }

    init();
})();
